import React, { useState, useEffect, useCallback, useMemo } from 'react';
import MathRenderer from '../math/MathRenderer';
import WorkedSolutionPanel from './WorkedSolutionPanel';
import SimilarQuestionPanel from './SimilarQuestionPanel';
import { QuestionApiModel } from '../../types/questions';
import { AttemptResult } from '../../types/practice';
import { formatRating, formatRatingChange } from '../../utils/format';
import { questionAPI } from '../../services/api';

interface QuestionCardProps {
  question: QuestionApiModel;
  onAnswer?: (selectedAnswer: string, timeSpentSeconds?: number) => void | Promise<void>;
  onNextQuestion?: () => void;
  showAnswer?: boolean;
  isSubmitting?: boolean;
  attemptResult?: AttemptResult | null;
  workedSolution?: string;
  initialSelectedAnswer?: string | null;
  showSimilarQuestion?: boolean;
  canGenerateSimilar?: boolean;
  showTimer?: boolean;
  onBookmarkChange?: (questionId: string, isBookmarked: boolean) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  onAnswer,
  onNextQuestion,
  showAnswer = false,
  isSubmitting = false,
  attemptResult = null,
  workedSolution,
  initialSelectedAnswer = null,
  showSimilarQuestion = false,
  canGenerateSimilar = false,
  showTimer = false,
  onBookmarkChange,
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mathRenderingCount, setMathRenderingCount] = useState(0);
  const [mathRenderingComplete, setMathRenderingComplete] = useState(0);
  const [animatedRating, setAnimatedRating] = useState<number | null>(null);
  const [timerStart, setTimerStart] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(Boolean(question.is_bookmarked));
  const [isBookmarkUpdating, setIsBookmarkUpdating] = useState(false);
  const [bookmarkError, setBookmarkError] = useState<string | null>(null);

  const effectiveWorkedSolution = useMemo(() => {
    return workedSolution ?? question.worked_solution ?? null;
  }, [workedSolution, question.worked_solution]);

  const formatProgressDate = (value: string) => {
    try {
      const parsedDate = new Date(value);
      if (Number.isNaN(parsedDate.getTime())) {
        return value;
      }
      return parsedDate.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return value;
    }
  };

  const formatTimer = (value: number) => {
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const statusPill = useMemo(() => {
    const progress = question.progress;
    if (!progress || !progress.last_attempt_at || progress.last_attempt_was_correct === null || progress.last_attempt_was_correct === undefined) {
      return {
        label: 'New',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      };
    }

    const formattedDate = formatProgressDate(progress.last_attempt_at);

    if (progress.last_attempt_was_correct) {
      return {
        label: `Last Solved Correctly on ${formattedDate}`,
        className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
      };
    }

    return {
      label: `Last Solved Incorrectly on ${formattedDate}`,
      className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    };
  }, [question.progress]);

  const displayOptions = useMemo(() => {
    const options = [
      { key: 'A', text: question.option_a },
      { key: 'B', text: question.option_b },
      { key: 'C', text: question.option_c },
      { key: 'D', text: question.option_d },
      ...(question.option_e ? [{ key: 'E', text: question.option_e }] : []),
    ];
    return options.filter(
      (option) => option.text && option.text.trim().length > 0
    );
  }, [
    question.option_a,
    question.option_b,
    question.option_c,
    question.option_d,
    question.option_e,
  ]);

  const marksForTimer = useMemo(() => {
    if (typeof question.marks === 'number' && question.marks > 0) {
      return question.marks;
    }
    return 1;
  }, [question.marks]);

  const idealTimeSeconds = useMemo(() => Math.round(marksForTimer * 90), [marksForTimer]);

  const mathRendererTotal = useMemo(() => {
    return 1 + displayOptions.length;
  }, [displayOptions.length]);

  // Calculate total number of MathRenderer components; only reset when question changes
  useEffect(() => {
    setMathRenderingCount(mathRendererTotal);
    setMathRenderingComplete(0);
  }, [mathRendererTotal, question.id]);

  const handleMathRenderComplete = useCallback(() => {
    setMathRenderingComplete(prev => prev + 1);
  }, []);

  const isAllMathRendered = mathRenderingComplete >= mathRenderingCount;

  // Animate rating change when attemptResult is set
  useEffect(() => {
    if (attemptResult) {
      setAnimatedRating(attemptResult.rating_before);
      const timer = setTimeout(() => {
        setAnimatedRating(attemptResult.rating_after);
      }, 800);
      return () => clearTimeout(timer);
    } else {
      setAnimatedRating(null);
    }
  }, [attemptResult]);

  useEffect(() => {
    if (!showTimer) {
      return;
    }
    setTimerStart(Date.now());
    setElapsedSeconds(0);
  }, [question.id, showTimer]);

  useEffect(() => {
    if (!showTimer || timerStart === null || isSubmitted || showAnswer) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - timerStart) / 1000));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [showTimer, timerStart, isSubmitted, showAnswer]);

  // Reset component state when a new question is shown
  useEffect(() => {
    setSelectedAnswer('');
    setIsSubmitted(false);
    setAnimatedRating(null);
  }, [question.id]);

  useEffect(() => {
    if (initialSelectedAnswer) {
      setSelectedAnswer(initialSelectedAnswer);
      setIsSubmitted(true);
    }
  }, [initialSelectedAnswer, question.id]);

  const handleAnswerSelect = (answer: string) => {
    if (isSubmitted || isSubmitting) return;
    setSelectedAnswer(answer);
  };

  const handleSubmit = async () => {
    if (!selectedAnswer || isSubmitted || isSubmitting) return;
    const timeSpentSeconds =
      showTimer && timerStart ? Math.floor((Date.now() - timerStart) / 1000) : undefined;
    if (showTimer && typeof timeSpentSeconds === 'number') {
      setElapsedSeconds(timeSpentSeconds);
    }

    setIsSubmitted(true);
    
    if (onAnswer) {
      await onAnswer(selectedAnswer, timeSpentSeconds);
    }
  };

  useEffect(() => {
    setIsBookmarked(Boolean(question.is_bookmarked));
    setBookmarkError(null);
  }, [question.id, question.is_bookmarked]);

  const handleBookmarkToggle = async () => {
    if (isBookmarkUpdating) return;

    const nextValue = !isBookmarked;
    setIsBookmarked(nextValue);
    setIsBookmarkUpdating(true);
    setBookmarkError(null);
    onBookmarkChange?.(question.id, nextValue);

    try {
      const response = nextValue
        ? await questionAPI.bookmarkQuestion(question.id)
        : await questionAPI.unbookmarkQuestion(question.id);
      setIsBookmarked(response.is_bookmarked);
      if (response.is_bookmarked !== nextValue) {
        onBookmarkChange?.(question.id, response.is_bookmarked);
      }
    } catch (error) {
      console.error('Failed to update bookmark', error);
      setIsBookmarked(!nextValue);
      onBookmarkChange?.(question.id, !nextValue);
      setBookmarkError('Could not update bookmark. Please try again.');
    } finally {
      setIsBookmarkUpdating(false);
    }
  };

  const getOptionStyle = (optionKey: string) => {
    if (!isSubmitted && !showAnswer) {
      return selectedAnswer === optionKey
        ? 'border-subject-500 bg-subject-50 dark:bg-subject-900/20'
        : 'border-gray-300 dark:border-gray-600 hover:border-subject-300';
    }

    if (optionKey === question.correct_answer) {
      return 'border-green-500 bg-green-50 dark:bg-green-900/20';
    }

    if (optionKey === selectedAnswer && optionKey !== question.correct_answer) {
      return 'border-red-500 bg-red-50 dark:bg-red-900/20';
    }

    return 'border-gray-300 dark:border-gray-600';
  };

  const getDifficultyColor = (rating: number | null) => {
    if (rating === null || rating === undefined) return 'text-gray-500 dark:text-gray-400';
    if (rating < 25) return 'text-green-600 dark:text-green-400';
    if (rating < 35) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getDifficultyLabel = (rating: number | null) => {
    if (rating === null || rating === undefined) return 'Unknown';
    if (rating < 25) return 'Easy';
    if (rating < 35) return 'Medium';
    return 'Hard';
  };

  const getProgressPercentage = (rating: number) => {
    // Convert rating (10-50) to percentage (0-100)
    return ((rating - 10) / (50 - 10)) * 100;
  };

  const options = displayOptions;

  const shouldRevealTags = (isSubmitted || showAnswer) && (question.tags?.length ?? 0) > 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-4xl mx-auto relative">
      {/* Loading overlay */}
      {!isAllMathRendered && (
        <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-90 flex items-center justify-center z-10 rounded-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-subject-500 mr-4"></div>
            <div className="text-lg text-gray-600 dark:text-gray-300">
              Loading question... ({mathRenderingComplete}/{mathRenderingCount})
            </div>
          </div>
        </div>
      )}
      {/* Question Header */}
      <div className="flex justify-between items-start mb-6 gap-4">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            {question.year} Exam {question.exam_type} · Q{question.question_number}
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">Difficulty:</span>
            <span className={`text-sm font-medium ${getDifficultyColor(question.difficulty_rating)}`}>
              {question.difficulty_rating === null || question.difficulty_rating === undefined
                ? 'Unknown'
                : `${getDifficultyLabel(question.difficulty_rating)} (${question.difficulty_rating})`}
            </span>
          </div>
          {showTimer && (
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
              <span>Time:</span>
              <span className={`font-semibold ${elapsedSeconds > idealTimeSeconds ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                {formatTimer(elapsedSeconds)}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                / {formatTimer(idealTimeSeconds)} ideal
              </span>
            </div>
          )}
        </div>
        <div className="flex items-start gap-3">
          {statusPill && (
            <span className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${statusPill.className}`}>
              {statusPill.label}
            </span>
          )}
          <button
            type="button"
            onClick={handleBookmarkToggle}
            disabled={isBookmarkUpdating}
            aria-pressed={isBookmarked}
            aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark question'}
            title={isBookmarked ? 'Remove bookmark' : 'Bookmark question'}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-60 ${
              isBookmarked
                ? 'border-subject-500 bg-subject-50 text-subject-700 dark:bg-subject-900/30 dark:text-subject-200'
                : 'border-gray-300 bg-white text-gray-600 hover:border-subject-400 hover:text-subject-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-subject-200'
            }`}
          >
            <span aria-hidden="true">{isBookmarked ? '🔖' : '♡'}</span>
            <span>{isBookmarked ? 'Bookmarked' : 'Bookmark'}</span>
          </button>
        </div>
      </div>

      {bookmarkError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-200">
          {bookmarkError}
        </div>
      )}

      {/* Question Text */}
      <div className="mb-6">
        <div className="text-lg text-gray-900 dark:text-white">
          <MathRenderer
            latex={question.question_text}
            onRenderComplete={handleMathRenderComplete}
          />
        </div>
      </div>

      {/* Options */}
      <div className="space-y-3 mb-6">
        {options.map((option) => {
          const isInactive = isSubmitted || showAnswer || isSubmitting;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => handleAnswerSelect(option.key)}
              aria-disabled={isInactive}
              tabIndex={isInactive ? -1 : 0}
              className={`w-full p-4 border-2 rounded-lg text-left transition-all duration-200 ${getOptionStyle(option.key)} ${
                !isInactive ? 'hover:shadow-sm cursor-pointer' : 'cursor-default'
              }`}
            >
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-medium">
                  {option.key}
                </span>
                <div className="flex-1 text-gray-900 dark:text-white">
                  <MathRenderer
                    latex={option.text}
                    onRenderComplete={handleMathRenderComplete}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Submit Button */}
      {!isSubmitted && !showAnswer && (
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={!selectedAnswer || isSubmitting}
            className="px-6 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Answer'}
          </button>
        </div>
      )}

      {/* Result */}
      {(isSubmitted || showAnswer) && (
        <div className="mt-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Correct Answer: {question.correct_answer}
            </span>
            {isSubmitted && (
              <span className={`text-sm font-medium ${
                selectedAnswer === question.correct_answer
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {selectedAnswer === question.correct_answer ? '✓ Correct!' : '✗ Incorrect'}
              </span>
            )}
          </div>

          {/* Rating Change - Show if attemptResult exists */}
          {attemptResult && animatedRating !== null && (
            <div className="mt-3 p-3 rounded-lg bg-white dark:bg-gray-600">
              <div className="mb-3">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Rating
                </div>

                {/* Rating Numbers */}
                <div className="flex items-center justify-center space-x-2 mb-3">
                  <span className="text-lg font-bold text-gray-700 dark:text-gray-300">
                    {formatRating(attemptResult.rating_before)}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span
                    className={`text-lg font-bold transition-all duration-500 ${
                      animatedRating === attemptResult.rating_after
                        ? 'text-gray-900 dark:text-white scale-110'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {formatRating(animatedRating)}
                  </span>
                  <span className={`text-sm font-semibold ${
                    attemptResult.rating_change > 0
                      ? 'text-green-600 dark:text-green-400'
                      : attemptResult.rating_change < 0
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    ({formatRatingChange(attemptResult.rating_change)})
                  </span>
                </div>
                {attemptResult.rating_bonus !== undefined && attemptResult.rating_bonus > 0 && (
                  <div className="flex justify-center mb-2">
                    <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200 px-3 py-1 text-xs font-semibold">
                      Speed Bonus {formatRatingChange(attemptResult.rating_bonus)}
                    </span>
                  </div>
                )}

                {/* Rating Progress Bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                  <div
                    className="bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${getProgressPercentage(animatedRating)}%` }}
                  />
                </div>

                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>Beginner (10)</span>
                  <span>Expert (50)</span>
                </div>
              </div>
            </div>
          )}

          {(isSubmitted || showAnswer) && (
            <div className="mt-4 space-y-3">
              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Area of Study</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">{question.vce_area_of_study}</div>
              </div>

              {shouldRevealTags && (
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tags
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {question.tags?.map((tag) => (
                      <span
                        key={tag.id}
                        className="px-2 py-1 text-xs font-medium rounded-full bg-subject-100 text-subject-800 dark:bg-subject-900/30 dark:text-subject-200"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Worked Solution */}
      {(isSubmitted || showAnswer) && (
        effectiveWorkedSolution ? (
          <WorkedSolutionPanel
            solution={effectiveWorkedSolution}
            isVisible
          />
        ) : (
          <div className="mt-4 px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
            Worked solution not available yet. We're working on adding solutions for every question.
          </div>
        )
      )}

      {showSimilarQuestion && (
        <SimilarQuestionPanel
          questions={[{ id: question.id, label: 'Question' }]}
          canGenerate={canGenerateSimilar}
        />
      )}

      {/* Next Question Button - Show if callback exists and answer is submitted */}
      {(isSubmitted || showAnswer) && onNextQuestion && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={onNextQuestion}
            className="px-6 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500 transition-colors font-medium"
          >
            Next Question
          </button>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
